import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { Archetype, SuggestionRequestWithRequester } from 'types'

interface Props {
  workspaceId: number
}

export interface Data {
  request: SuggestionRequestWithRequester
}

export interface Variables {
  source_visual_element_id?: number
  topic?: string
  brand_voice_id?: number
  tone?: Array<string>
  reference_text?: string
  language?: string
  brand_kit_id?: number
  brand_visual_kit_id?: number
  signal?: AbortSignal
  initial_media_file_id?: string
  initial_media_file_remove_background?: boolean
  apply_brand?: boolean
  archetype?: Archetype
  width?: number
  height?: number
  media_priority?: string
  featured_media?: string[]
}

export type Options = Omit<
  UseMutationOptions<SuggestionRequestWithRequester, unknown, Variables>,
  'mutationKey' | 'mutationFn'
>

const generateDesignContent = async (workspaceId: number, variables: Variables) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/designs/generate_content`, {
    method: 'POST',
    body: {
      prompt: {
        topic: variables.topic,
        brand_voice_id: variables.brand_voice_id,
        tone: variables.tone,
        reference_text: variables.reference_text,
        language: variables.language,
        source_visual_element_id: variables.source_visual_element_id,
        brand_kit_id: variables.brand_kit_id,
        brand_visual_kit_id: variables.brand_visual_kit_id,
        initial_media_file_id: variables.initial_media_file_id,
        initial_media_file_remove_background: variables.initial_media_file_remove_background,
        apply_brand: variables.apply_brand,
        generation_type: 'design_content',
        archetype: variables.archetype,
        width: variables.width,
        height: variables.height,
        media_priority: variables.media_priority,
        featured_media: variables.featured_media,
      },
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
      'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data.request
}

export const useGenerateDesignContent = ({ workspaceId }: Props, options?: Options) => {
  return useMutation<SuggestionRequestWithRequester, unknown, Variables>(
    'generateDesignContent',
    async (variables) =>
      generateDesignContent(workspaceId, {
        ...variables,
        brand_kit_id: variables.brand_kit_id,
        brand_visual_kit_id: undefined,
      }),
    options,
  )
}
